import React from "react";
import Report from "../report/report";
import {Utils} from "../../../helpers/utils";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {IPaginationStore} from "../../../common/stores/paginationStore";
import {storesContext} from "../../../common/stores/stores";
import i18n from "../../../i18n/i18n";
import DropDown from "../../../common/components/shared/dropDown/dropDown";
import {ValidatedValue} from "../../../helpers/validatedValue";
import {DropDownModel} from "../../../common/models/dropDown";
import {FerryDropDownModel} from "../../../dictionaries/models/ferry";
import {FerryService} from "../../../dictionaries/services/ferryService";
import {DropDownUtils} from "../../../helpers/dropDownUtils";
import {InvoiceAttachmentModel, InvoiceAttachmentSummaryModel, InvoiceAttachmentFilters} from "../../models/invoiceAttachment";
import {DateUtils} from "../../../helpers/dateUtils";
import {InvoiceAttachmentService} from "../../services/invoiceAttachmentService";
import {Api} from "../../../helpers/api";
import DateTimePicker from "../../../common/components/shared/dateTimePicker/dateTimePicker";
import Validator from "../../../helpers/validator";
import {errorMessages} from "../../../helpers/errorMessage";
import Loader from "../../../common/components/shared/loader/loader";

interface InvoiceAttachmentState {
    selectedFerry: ValidatedValue<string>;
    selectedDateFrom: ValidatedValue<Date | null>;
    selectedDateTo: ValidatedValue<Date | null>;
    isSubmitButtonBlocked: boolean;
    isReportGenerated: boolean;
    generatedReport: InvoiceAttachmentModel[] | undefined | null;
    generatedReportResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isSummaryReportGenerated: boolean;
    generatedSummaryReport: InvoiceAttachmentSummaryModel | undefined | null;
    generatedSummaryReportResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
    ferries: FerryDropDownModel[];
    ferriesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

const notEmptyValidator = {
    name: "valueIsNotEmpty",
    fn: Validator.isNotEmpty,
    errorMessage: errorMessages.required
};

export default class InvoiceAttachment extends React.Component <any, InvoiceAttachmentState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            selectedFerry: new ValidatedValue(""),
            selectedDateFrom: new ValidatedValue(null, [notEmptyValidator]),
            selectedDateTo: new ValidatedValue(null, [notEmptyValidator]),
            isSubmitButtonBlocked: false,
            isReportGenerated: false,
            generatedReport: undefined,
            generatedReportResponse: undefined,
            currentPage: 0,
            ferries: [],
            isSummaryReportGenerated: false,
            generatedSummaryReport: undefined,
            generatedSummaryReportResponse: undefined,
            ferriesResponse: undefined
        };
    }

    async componentDidMount() {
        await this.getFerries();
    }

    public render(): JSX.Element {
        const properties = ["ferryName", "directionType", "voyageNo", "arrivalDepartureTime",
            "numberOfPassengers", "numberOfDrivers", "numberOfLorries", "numberOfTrailers",
            "numberOfCars", "numberOfBuses", "numberOfWagons", "numberOfContainers",
            "numberOfGeneralCargos", "numberOfOthers", "numberOfBillOfLading"];
        const headers = properties.map(key => i18n.t("reports.invoiceAttachment.headers." + key));
        const reportSummaryProperties = ["numberOfPassengers", "numberOfDrivers", "numberOfLorries", "numberOfTrailers",
            "numberOfCars", "numberOfBuses", "numberOfWagons", "numberOfContainers",
            "numberOfGeneralCargos", "numberOfOthers", "numberOfBillOfLading"];
        const reportSummaryHeaders = reportSummaryProperties.map(key => i18n.t("reports.invoiceAttachment.headers." + key));

        if (this.state.ferries.length > 0 || !Api.isSuccess(this.state.ferriesResponse)) {
            return (
                <Report
                    className="invoice-attachment"
                    filters={this.renderFilters()}
                    onGenerateReport={this.onGenerateReport.bind(this)}
                    generatedReport={this.state.generatedReport}
                    generatedReportResponse={this.state.generatedReportResponse}
                    isReportGenerated={this.state.isReportGenerated}
                    reportTestId="invoice-attachment"
                    reportProperties={properties}
                    reportHeaders={headers}
                    reportTableRenderCell={this.renderCell}
                    currentPage={this.state.currentPage}
                    reportService={InvoiceAttachmentService}
                    isSummaryReportGenerated={this.state.isSummaryReportGenerated}
                    generatedSummaryReport={[this.state.generatedSummaryReport]}
                    generatedSummaryReportResponse={this.state.generatedSummaryReportResponse}
                    reportSummaryHeaders={reportSummaryHeaders}
                    reportSummaryProperties={reportSummaryProperties}
                    selectedFilters={
                        {
                            dateFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDateFrom.value),
                            dateTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDateTo.value),
                            ferryEntityId: this.state.selectedFerry.value
                        } as InvoiceAttachmentFilters
                    }
                    isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                    summaryCurrentPage={0}
                    summaryNumberOfElements={0}
                />
            );
        } else {
            return <Loader/>;
        }
    }

    private renderFilters(): JSX.Element {
        return (
            <>
                <DateTimePicker
                    label={i18n.t("reports.invoiceAttachment.filters.dateFrom")}
                    validatedValue={this.state.selectedDateFrom}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedDateFrom.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedDateFrom, this.state.selectedDateTo);
                        this.addNotEmptyValidator(this.state.selectedDateFrom, this.state.selectedDateTo);
                    }}
                    testId="filters-date-from"
                />
                <DateTimePicker
                    label={i18n.t("reports.invoiceAttachment.filters.dateTo")}
                    validatedValue={this.state.selectedDateTo}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedDateTo.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedDateFrom, this.state.selectedDateTo);
                        this.addNotEmptyValidator(this.state.selectedDateFrom, this.state.selectedDateTo);
                    }}
                    testId="filters-date-to"
                />
                <DropDown
                    label={i18n.t("reports.invoiceAttachment.filters.ferry")}
                    items={this.ferryToDropDownMap}
                    validatedValue={this.state.selectedFerry}
                    testId="filters-ferry"
                    withEmptyValue
                    error={!Api.isSuccess(this.state.ferriesResponse) ? i18n.t("errorMessages.ferriesErrorSuffix") : ""}
                    reloadFunc={() => this.getFerries()}
                    isNoRequired
                />
            </>
        );
    }

    private renderCell(row: any, property: string): JSX.Element {
        switch (property) {
            case "directionType":
                return <>{i18n.t(`directionType.${row[property]}`)}</>;
            case "arrivalDepartureTime":
                return <>{DateUtils.getFormattedDateWithTime((row[property]))}</>;
            default:
                return row[property];
        }
    }

    private get ferryToDropDownMap(): DropDownModel[] {
        return DropDownUtils.listToDropDownMap(this.state.ferries, "entityId");
    }

    private addNotEmptyValidator(from: ValidatedValue<Date | null>, to: ValidatedValue<Date | null>) {
        from.addValidators([notEmptyValidator]);
        to.addValidators([notEmptyValidator]);
    }

    private get isValid(): boolean {
        this.state.selectedFerry.validate();
        this.state.selectedDateFrom.validate();
        this.state.selectedDateTo.validate();

        return this.state.selectedFerry.isValid && this.state.selectedDateFrom.isValid
        && this.state.selectedDateTo.isValid;
    }

    private async onGenerateReport() {
        this.setState({generatedReportResponse: undefined});
        const paginationStore = this.context.paginationStore as IPaginationStore;

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});

            const filters = {
                dateFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDateFrom.value),
                dateTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDateTo.value),
                ferryEntityId: this.state.selectedFerry.value
            };

            const generatedReportResponse = await InvoiceAttachmentService.generateReport(
                filters
            );

            this.setState({
                generatedReportResponse,
                generatedReport: generatedReportResponse?.data?.data || null,
                isReportGenerated: true,
                currentPage: generatedReportResponse?.data?.currentPage
            });

            if (Api.isSuccess(generatedReportResponse)) {
                const generatedSummaryReportResponse = await InvoiceAttachmentService.generateSummaryReport(filters);

                this.setState({
                    generatedSummaryReportResponse,
                    generatedSummaryReport: generatedSummaryReportResponse?.data || null,
                    isSummaryReportGenerated: true
                });
            }

            paginationStore.setNumberOfElements(generatedReportResponse?.data?.numberOfElements);
            paginationStore.setNumberOfPages(generatedReportResponse?.data?.numberOfPages);
        }

        Utils.scrollToFirstErrorMessage();
        this.setState({isSubmitButtonBlocked: false});
    }

    private async getFerries() {
        const ferriesResponse = await FerryService.getFerriesForDropDown();

        this.setState({
            ferries: (ferriesResponse)?.data || [],
            ferriesResponse,
        });
    }
}